import {
  Download,
  ExternalLink,
  Eye,
  FileText,
  Paperclip,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { apiBlobRequest } from "../../../../shared/api/client";
import type { RequestWorkspaceService } from "../../application/queries/request-workspace";
import type {
  RequestAttachment,
  RequestWorkspaceOffer,
} from "../../domain/models";

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function scanStatusLabel(status: string) {
  switch (status) {
    case "CLEAN":
      return "Zararlı yazılım taraması tamamlandı";
    case "VALIDATED":
      return "Dosya türü ve aktif içerik kontrolü tamamlandı";
    case "DEMO_SAFE":
      return "Demo ortamında doğrulandı";
    case "PENDING":
      return "İlk açılışta güvenlik kontrolü yapılacak";
    case "BLOCKED":
      return "Erişim engellendi";
    case "FAILED":
      return "Güvenlik kontrolü tamamlanamadı";
    default:
      return "Güvenlik durumu bilinmiyor";
  }
}

function isAccessible(attachment: RequestAttachment) {
  return ["PENDING", "VALIDATED", "CLEAN", "DEMO_SAFE"].includes(
    attachment.scanStatus,
  );
}

function isPreviewable(attachment: RequestAttachment) {
  return (
    attachment.contentType === "application/pdf" ||
    attachment.contentType.startsWith("image/")
  );
}

function previewUrl(downloadUrl: string) {
  return `${downloadUrl}${downloadUrl.includes("?") ? "&" : "?"}preview=1`;
}

export function OfferDocumentsInline({
  attachments,
}: {
  attachments: RequestAttachment[];
}) {
  const [open, setOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] =
    useState<RequestAttachment | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState("");
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (!previewAttachment) {
      setPreviewObjectUrl("");
      setPreviewError("");
      return;
    }
    let active = true;
    let objectUrl = "";
    apiBlobRequest(
      `/api/attachments/${encodeURIComponent(previewAttachment.id)}?preview=1`,
    )
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewObjectUrl(objectUrl);
      })
      .catch((error) => {
        if (!active) return;
        setPreviewError(
          error instanceof Error
            ? error.message
            : "Belge önizlemesi açılamadı.",
        );
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewAttachment]);

  if (!attachments.length) return null;

  return (
    <div
      className="detail-offer-documents manager-offer-documents"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="detail-offer-documents-trigger"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Paperclip size={13} aria-hidden="true" />
        {attachments.length} belge
      </button>
      {open ? (
        <div className="detail-offer-documents-menu" role="menu">
          <strong>Teklif belgeleri</strong>
          {attachments.map((attachment) => {
            const previewable =
              isAccessible(attachment) && isPreviewable(attachment);
            return (
              <button
                type="button"
                role="menuitem"
                key={attachment.id}
                title={attachment.name}
                disabled={!previewable}
                onClick={() => {
                  setOpen(false);
                  setPreviewAttachment(attachment);
                }}
              >
                <FileText size={14} aria-hidden="true" />
                <span>{attachment.name}</span>
                {previewable ? <Eye size={14} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
      {previewAttachment ? (
        <div
          className="attachment-preview-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setPreviewAttachment(null);
          }}
        >
          <section
            className="attachment-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="manager-attachment-preview-title"
          >
            <header>
              <div>
                <small>Teklif belgesi</small>
                <strong id="manager-attachment-preview-title">
                  {previewAttachment.name}
                </strong>
              </div>
              <button
                type="button"
                aria-label="Belge önizlemesini kapat"
                onClick={() => setPreviewAttachment(null)}
                autoFocus
              >
                <X size={20} aria-hidden="true" />
              </button>
            </header>
            <div className="attachment-preview-content">
              {previewError ? (
                <div className="attachment-preview-error" role="alert">
                  <FileText size={28} aria-hidden="true" />
                  <strong>Belge açılamadı</strong>
                  <p>{previewError}</p>
                </div>
              ) : !previewObjectUrl ? (
                <div className="attachment-preview-loading" role="status">
                  Belge hazırlanıyor…
                </div>
              ) : previewAttachment.contentType.startsWith("image/") ? (
                <img src={previewObjectUrl} alt={previewAttachment.name} />
              ) : (
                <iframe src={previewObjectUrl} title={previewAttachment.name} />
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function attachmentSourceLabel(
  attachment: RequestAttachment,
  groupedByOffer: boolean,
) {
  if (attachment.offerNo)
    return groupedByOffer
      ? "Teklif belgesi"
      : `Teklif belgesi · ${attachment.offerNo}`;
  const source =
    attachment.uploadedByRole === "PLANLAMA"
      ? "Planlama belgesi"
      : attachment.uploadedByRole === "SATIN_ALMA"
        ? "Satın Alma belgesi"
        : "Talep belgesi";
  return attachment.uploadedBy
    ? `${source} · ${attachment.uploadedBy}`
    : source;
}

export function RequestDocuments({
  attachments,
  offers = [],
}: {
  attachments: RequestAttachment[];
  offers?: RequestWorkspaceOffer[];
}) {
  const offerLookup = new Map(
    offers.map((offer) => [offer.offerNo, offer.supplier]),
  );
  const groups = attachments.reduce<
    Array<{
      key: string;
      offerNo: string | null;
      supplier: string | null;
      attachments: RequestAttachment[];
    }>
  >((result, attachment) => {
    const key = attachment.offerNo ?? "REQUEST";
    const existing = result.find((group) => group.key === key);
    if (existing) {
      existing.attachments.push(attachment);
      return result;
    }
    result.push({
      key,
      offerNo: attachment.offerNo,
      supplier: attachment.offerNo
        ? (offerLookup.get(attachment.offerNo) ?? null)
        : null,
      attachments: [attachment],
    });
    return result;
  }, []);

  return (
    <section
      className={`request-documents${attachments.length === 0 ? " is-empty" : ""}`}
      aria-label="Talep belgeleri"
    >
      {attachments.length ? (
        <>
          <header className="request-documents-heading">
            <span>
              <Paperclip size={16} />
              <strong>Belgeler</strong>
            </span>
            <small>{attachments.length} belge</small>
          </header>
          <div className="request-document-groups">
            {groups.map((group) => (
              <section className="request-document-group" key={group.key}>
                <header className="request-document-group-heading">
                  <span>
                    <strong>{group.offerNo ?? "Talep belgeleri"}</strong>
                    {group.offerNo ? (
                      <small>
                        {group.supplier ?? "Tedarikçi bilgisi bulunamadı"}
                      </small>
                    ) : (
                      <small>Herhangi bir teklife bağlı olmayan belgeler</small>
                    )}
                  </span>
                  <small>{group.attachments.length} belge</small>
                </header>
                <div className="request-document-list">
                  {group.attachments.map((attachment) => {
                    const accessible = isAccessible(attachment);
                    const previewable = accessible && isPreviewable(attachment);
                    return (
                      <article
                        className="request-document-row"
                        key={attachment.id}
                      >
                        <span
                          className="request-document-icon"
                          aria-hidden="true"
                        >
                          <FileText size={17} />
                        </span>
                        <span className="request-document-identity">
                          <strong>{attachment.name}</strong>
                          <small className="request-document-source">
                            {attachmentSourceLabel(attachment, true)}
                          </small>
                          <small>
                            {formatBytes(attachment.sizeBytes)} ·{" "}
                            {scanStatusLabel(attachment.scanStatus)}
                          </small>
                        </span>
                        <span className="request-document-actions">
                          {previewable ? (
                            <a
                              href={previewUrl(attachment.downloadUrl)}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`${attachment.name} belgesini aç`}
                            >
                              <ExternalLink size={14} /> Aç
                            </a>
                          ) : null}
                          {accessible ? (
                            <a
                              href={attachment.downloadUrl}
                              download
                              aria-label={`${attachment.name} belgesini indir`}
                            >
                              <Download size={14} /> İndir
                            </a>
                          ) : (
                            <span className="request-document-locked">
                              Erişim bekliyor
                            </span>
                          )}
                        </span>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </>
      ) : (
        <div className="request-documents-empty-row">
          <Paperclip size={18} strokeWidth={1.8} aria-hidden="true" />
          <span>
            <strong>Belgeler</strong>
            <small>Teklif koşullarını destekleyen ek dosya yok</small>
          </span>
          <b>0 belge</b>
        </div>
      )}
    </section>
  );
}

export function RequestDocumentsPanel({
  requestNo,
  service,
  refreshKey = 0,
}: {
  requestNo: string;
  service: RequestWorkspaceService;
  refreshKey?: number;
}) {
  const [documents, setDocuments] = useState<{
    attachments: RequestAttachment[];
    offers: RequestWorkspaceOffer[];
  } | null>(null);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setDocuments(null);
    setError("");
    service
      .load(requestNo)
      .then((detail) => {
        if (active) {
          setDocuments({
            attachments: detail.attachments,
            offers: detail.offers ?? [],
          });
        }
      })
      .catch((loadError) => {
        if (!active) return;
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Belgeler yüklenemedi.",
        );
      });
    return () => {
      active = false;
    };
  }, [refreshKey, reloadVersion, requestNo, service]);

  if (error) {
    return (
      <section
        className="request-documents request-documents-state"
        role="alert"
      >
        <span>Belgeler yüklenemedi.</span>
        <button
          type="button"
          className="secondary-button"
          onClick={() => setReloadVersion((current) => current + 1)}
        >
          Yeniden dene
        </button>
      </section>
    );
  }

  if (!documents) {
    return (
      <section className="request-documents request-documents-state">
        Belgeler yükleniyor…
      </section>
    );
  }

  return (
    <RequestDocuments
      attachments={documents.attachments}
      offers={documents.offers}
    />
  );
}
